/*
 * 100k-Aktion giveaway page (sections/aktion-100k.liquid).
 *
 * Talks to the giveaway app through the app proxy (elusa-gewinnspiel-app, README
 * "Redeeming" and "Bank details"). Every request is a POST with a JSON body of strings
 * and answers { ok: true, … } or { ok: false, error }.
 *
 * Privacy: never log IBANs, account holders, email addresses, codes or tokens. This file
 * doesn't log at all.
 */
(() => {
  'use strict';

  // The theme editor runs the section's script again whenever it re-renders the section.
  // A second copy would bind every button twice (a help toggle would open and close at once);
  // the first copy already handles re-rendered sections via shopify:section:load.
  if (window.aktion100kLoaded) return;
  window.aktion100kLoaded = true;

  // Also written by the script at the top of layout/theme.liquid.
  const TOKEN_STORAGE_KEY = 'gw100k:bankDetailsToken';
  const REQUEST_TIMEOUT_MS = 20000;

  // Letters and digits only: which characters the printed cards use isn't confirmed, so no
  // character is ruled out here. The app tells whether the code exists.
  const CARD_CODE_PATTERN = /^[A-Z0-9]{8}$/;
  const EMAIL_CODE_PATTERN = /^[0-9A-F]{6}$/;
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // ---------------------------------------------------------------------------------------
  // IBAN check, the one shared function for the cash prize and the bank details link.
  // Mirrors app/services/iban.ts of the app: SEPA countries only, national format and
  // length per country (SWIFT IBAN registry), check digits 02–98 and mod 97.
  // ---------------------------------------------------------------------------------------

  // n = digit, a = uppercase letter, c = letter or digit.
  const SEPA_BBAN_FORMATS = {
    AD: '4n4n12c', AL: '8n16c', AT: '16n', BE: '12n', BG: '4a6n8c', CH: '5n12c', CY: '8n16c',
    CZ: '20n', DE: '18n', DK: '14n', EE: '16n', ES: '20n', FI: '14n', FR: '10n11c2n',
    GB: '4a14n', GI: '4a15c', GR: '7n16c', HR: '17n', HU: '24n', IE: '4a14n', IS: '22n',
    IT: '1a10n12c', LI: '5n12c', LT: '16n', LU: '3n13c', LV: '4a13c', MC: '10n11c2n',
    MD: '20c', ME: '18n', MK: '3n10c2n', MT: '4a5n18c', NL: '4a10n', NO: '11n', PL: '24n',
    PT: '21n', RO: '4a16c', RS: '18n', SE: '20n', SI: '15n', SK: '20n', SM: '1a10n12c',
    VA: '18n',
  };
  const BBAN_CHARACTER_CLASSES = { n: '[0-9]', a: '[A-Z]', c: '[A-Z0-9]' };
  const BBAN_PATTERNS = Object.fromEntries(
    Object.entries(SEPA_BBAN_FORMATS).map(([country, format]) => [
      country,
      new RegExp(
        `^${format.replace(/(\d+)([nac])/g, (_, count, type) => `${BBAN_CHARACTER_CLASSES[type]}{${count}}`)}$`,
      ),
    ]),
  );

  function normalizeIban(input) {
    return String(input || '').replace(/[\s-]/g, '').toUpperCase();
  }

  // Returns null for a valid IBAN, otherwise the error code the app would answer.
  function validateIban(input) {
    const iban = normalizeIban(input);
    if (!iban) return 'empty';
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return 'invalidIban';
    const checkDigits = Number(iban.slice(2, 4));
    if (checkDigits < 2 || checkDigits > 98) return 'invalidIban';
    const bban = BBAN_PATTERNS[iban.slice(0, 2)];
    if (!bban) return 'ibanNotSepa';
    if (!bban.test(iban.slice(4)) || !hasValidIbanCheckDigits(iban)) return 'invalidIban';
    return null;
  }

  function hasValidIbanCheckDigits(iban) {
    const rearranged = iban.slice(4) + iban.slice(0, 4);
    let remainder = 0;
    for (const char of rearranged) {
      const value = char >= 'A' ? char.charCodeAt(0) - 55 : Number(char);
      remainder = (remainder * (value >= 10 ? 100 : 10) + value) % 97;
    }
    return remainder === 1;
  }

  // Full IBAN length per SEPA country: country, check digits and the account part.
  const IBAN_LENGTHS = Object.fromEntries(
    Object.entries(SEPA_BBAN_FORMATS).map(([country, format]) => [
      country,
      4 + format.match(/\d+/g).reduce((sum, count) => sum + Number(count), 0),
    ]),
  );
  const IBAN_MAX_LENGTH = 34;

  // Errors that are certain while the IBAN is still being typed: a country outside SEPA as
  // soon as it's there, everything else once the IBAN has its full length.
  function validateIbanWhileTyping(input) {
    const iban = normalizeIban(input);
    const country = iban.slice(0, 2);
    if (/^[A-Z]{2}$/.test(country) && !BBAN_PATTERNS[country]) return 'ibanNotSepa';
    if (iban.length === IBAN_LENGTHS[country]) return validateIban(iban);
    return null;
  }

  // Groups of four for reading: "DE89 3704 0044 0532 0130 00".
  function formatIban(input) {
    return normalizeIban(input).replace(/(.{4})/g, '$1 ').trim();
  }

  // The app rejects names it can't write in the SEPA character set (e.g. Cyrillic or
  // Chinese) and names over 70 characters. Checked loosely here; the app has the last word.
  function validateAccountHolder(input) {
    const name = String(input || '').trim();
    if (!name) return 'empty';
    if (name.length < 2 || name.length > 70) return 'invalidAccountHolder';
    if (!/^[\p{Script=Latin}\p{M}0-9\s'’.,\-/&+():?]+$/u.test(name)) return 'invalidAccountHolder';
    return null;
  }

  // Card codes are stored without spaces and hyphens, in uppercase.
  function normalizeCode(input) {
    return String(input || '').replace(/[\s-]/g, '').toUpperCase();
  }

  // For display only: "3FA9C1" → "3FA-9C1". Requests always get the code without hyphen.
  function groupCode(code, groupSize) {
    return code.length > groupSize ? `${code.slice(0, groupSize)}-${code.slice(groupSize)}` : code;
  }

  // ---------------------------------------------------------------------------------------
  // Period, dates and amounts
  // ---------------------------------------------------------------------------------------

  // "2026-11-01T00:00+01:00" → { time, offsetMinutes }; null if it can't be read. Only ISO
  // with a time zone: browsers parse other text differently, and without a zone it would be
  // read in the visitor's time zone.
  function parseDateSetting(value) {
    const text = String(value || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?([+-]\d{2}:?\d{2}|Z)$/.test(text)) return null;
    const time = Date.parse(text);
    if (Number.isNaN(time)) return null;
    const offset = text.match(/([+-])(\d{2}):?(\d{2})$/);
    const offsetMinutes = offset
      ? (offset[1] === '-' ? -1 : 1) * (Number(offset[2]) * 60 + Number(offset[3]))
      : 0;
    return { time, offsetMinutes };
  }

  // Formats an instant in the time zone offset it was entered with, so the date shown is
  // the one the setting means and not the visitor's local one.
  function formatInOffset(time, offsetMinutes, locale, options) {
    const shifted = new Date(time + offsetMinutes * 60000);
    try {
      return shifted.toLocaleDateString(locale, { ...options, timeZone: 'UTC' });
    } catch (error) {
      return shifted.toISOString().slice(0, 10);
    }
  }

  function formatAmount(amountCents, locale) {
    const cents = Number(amountCents);
    if (!Number.isFinite(cents)) return '';
    const fractionDigits = cents % 100 === 0 ? 0 : 2;
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(fractionDigits)} €`;
    }
  }

  function fill(template, values) {
    return String(template || '').replace(/\{(\w+)\}/g, (match, key) =>
      key in values ? String(values[key]) : match,
    );
  }

  function prefersReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (error) {
      return false;
    }
  }

  // ---------------------------------------------------------------------------------------
  // Bank details token from the email link (#token=…)
  // ---------------------------------------------------------------------------------------

  const tokenStore = {
    read() {
      try {
        return sessionStorage.getItem(TOKEN_STORAGE_KEY) || '';
      } catch (error) {
        return '';
      }
    },
    write(token) {
      try {
        sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
      } catch (error) {
        // Private mode without storage: the token stays in memory for this visit.
      }
    },
    clear() {
      try {
        sessionStorage.removeItem(TOKEN_STORAGE_KEY);
      } catch (error) {
        // Nothing stored.
      }
    },
  };

  // Moves the token from the address bar into sessionStorage, so it doesn't stay in the
  // history or get shared with the URL. Returns the token, also when storage isn't available.
  // On the page template 100k-aktion the script at the top of layout/theme.liquid has done this
  // already, before any other script could read the URL; this covers other templates.
  function takeTokenFromHash() {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return '';
    const token = new URLSearchParams(hash).get('token') || '';
    if (!token) return '';
    tokenStore.write(token);
    history.replaceState(null, '', location.pathname + location.search);
    return token;
  }

  // ---------------------------------------------------------------------------------------
  // API
  // ---------------------------------------------------------------------------------------

  function asStrings(body) {
    return Object.fromEntries(
      Object.entries(body)
        .filter(([, value]) => value !== undefined && value !== null)
        .map(([key, value]) => [key, String(value)]),
    );
  }

  async function postToProxy(proxyPath, action, body) {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null;
    try {
      const response = await fetch(`${proxyPath.replace(/\/+$/, '')}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(asStrings(body)),
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller ? controller.signal : undefined,
      });
      let data = null;
      try {
        data = await response.json();
      } catch (error) {
        data = null;
      }
      if (data && typeof data.ok === 'boolean') return data;
      return { ok: false, error: 'network' };
    } catch (error) {
      return { ok: false, error: 'network' };
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  /*
   * Simulated answers for testing every state in the theme editor without the app
   * (setting "Antworten simulieren", only active in the theme editor):
   * - verify-order, online order number: 1 wrongEmailCode, 2 orderAlreadyUsed,
   *   3 tooManyAttempts, 4 notStarted, 5 ended, 6 unavailable, 7 invalidInput, 9 network error.
   * - verify-order, TikTok order ID: 1 unknownOrder, 2 orderAlreadyUsed, 3 tooManyAttempts,
   *   8 invalidEmail.
   * - redeem, card code: AAAAAAAA cash with token, BBBBBBBB cash with bank details in,
   *   CCCCCCCC invalidCode, DDDDDDDD alreadyRedeemed, EEEEEEEE orderAlreadyUsed,
   *   FFFFFFFF tooManyAttempts, GGGGGGGG discountUnavailable, HHHHHHHH deadlinePassed,
   *   JJJJJJJJ ended, anything else the discount.
   * - bank-details, account holder: LINKUSED, INVALIDLINK, DEADLINE, NOCASH, FEHLER.
   * - bank-details-link, token (#token=…): used, invalid, deadline, error.
   */
  function simulatedResponse(action, body) {
    const fail = (error) => ({ ok: false, error });
    if (action === 'verify-order' || action === 'redeem') {
      const key = body.channel === 'tiktok' ? body.tiktokOrderId : String(body.orderName).replace('#', '');
      const orderErrors =
        body.channel === 'tiktok'
          ? { 1: 'unknownOrder', 2: 'orderAlreadyUsed', 3: 'tooManyAttempts', 8: 'invalidEmail' }
          : {
              1: 'wrongEmailCode', 2: 'orderAlreadyUsed', 3: 'tooManyAttempts', 4: 'notStarted',
              5: 'ended', 6: 'unavailable', 7: 'invalidInput', 9: 'network',
            };
      if (action === 'verify-order') return orderErrors[key] ? fail(orderErrors[key]) : { ok: true };
      const codeErrors = {
        CCCCCCCC: 'invalidCode', DDDDDDDD: 'alreadyRedeemed', EEEEEEEE: 'orderAlreadyUsed',
        FFFFFFFF: 'tooManyAttempts', GGGGGGGG: 'discountUnavailable', HHHHHHHH: 'deadlinePassed',
        JJJJJJJJ: 'ended',
      };
      if (codeErrors[body.code]) return fail(codeErrors[body.code]);
      if (body.code === 'AAAAAAAA') {
        return { ok: true, prize: { type: 'cash', amountCents: 25000 }, bankDetailsToken: 'simulated' };
      }
      if (body.code === 'BBBBBBBB') return { ok: true, prize: { type: 'cash', amountCents: 10000 } };
      return { ok: true, prize: { type: 'discount', discountCode: 'ELUSA-7QX4-M2PZ' } };
    }
    if (action === 'bank-details') {
      const errors = {
        LINKUSED: 'linkUsed', INVALIDLINK: 'invalidLink', DEADLINE: 'deadlinePassed',
        NOCASH: 'noCashPrize', FEHLER: 'unavailable',
      };
      const error = errors[String(body.accountHolder).toUpperCase()];
      if (error) return fail(error);
      const iban = normalizeIban(body.iban);
      return { ok: true, ibanMasked: `${iban.slice(0, 4)} … ${iban.slice(-4)}` };
    }
    if (action === 'bank-details-link') {
      const errors = {
        used: 'linkUsed', invalid: 'invalidLink', deadline: 'deadlinePassed',
        error: 'network',
      };
      return errors[body.token] ? fail(errors[body.token]) : { ok: true, amountCents: 5000 };
    }
    return fail('methodNotAllowed');
  }

  // ---------------------------------------------------------------------------------------
  // Page
  // ---------------------------------------------------------------------------------------

  class Aktion100k {
    constructor(root) {
      this.root = root;
      this.locale = root.dataset.locale || document.documentElement.lang || 'de';
      this.proxyPath = root.dataset.proxy || '/apps/gewinnspiel';
      this.simulate = root.hasAttribute('data-simulate');
      this.start = parseDateSetting(root.dataset.start);
      this.end = parseDateSetting(root.dataset.end);
      this.mode = root.dataset.mode || 'auto';
      try {
        this.texts = JSON.parse(root.querySelector('[data-gw100k-i18n]').textContent);
      } catch (error) {
        this.texts = { fields: {}, banners: {}, token: {}, countdown: '' };
      }

      this.card = root.querySelector('[data-card]');
      this.live = root.querySelector('[data-live]');
      this.views = Array.from(root.querySelectorAll('[data-view]'));
      this.forms = {
        s1: root.querySelector('[data-view="s1"]'),
        s2: root.querySelector('[data-view="s2"]'),
        pay: root.querySelector('[data-pay-form]'),
      };

      this.state = {
        view: 'boot',
        channel: 'online',
        order: null, // the checked order, sent again with redeem
        amountCents: null,
        bankDetailsToken: '',
        busy: false,
      };
      this.lastAction = null;
      this.linkToken = ''; // token of the last link check, for its retry button
      this.countdownTimer = null;
      this.confettiTimer = null;

      this.bindEvents();
      this.fillDates();

      const preview = root.dataset.preview;
      if (preview) {
        this.showPreview(preview);
        return;
      }

      const token = window.aktion100kBankDetailsToken || takeTokenFromHash() || tokenStore.read();
      if (token) {
        this.loadToken(token);
        return;
      }
      this.showForPeriod(false);
    }

    destroy() {
      clearInterval(this.countdownTimer);
      clearTimeout(this.confettiTimer);
    }

    // ----- Requests ----------------------------------------------------------------------

    async request(action, body) {
      if (this.simulate) {
        await new Promise((resolve) => setTimeout(resolve, 700));
        return simulatedResponse(action, asStrings(body));
      }
      return postToProxy(this.proxyPath, action, body);
    }

    // Runs one request for a form: loading state on its button, no second request while
    // one is running (double clicks, Enter pressed twice).
    async run(form, action, body) {
      if (this.state.busy) return null;
      this.state.busy = true;
      const button = form.querySelector('[data-submit]');
      button.classList.add('is-busy');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      try {
        return await this.request(action, body);
      } finally {
        this.state.busy = false;
        button.classList.remove('is-busy');
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }

    // ----- Views -------------------------------------------------------------------------

    show(view, { focus = true } = {}) {
      this.state.view = view;
      this.views.forEach((element) => {
        element.hidden = element.dataset.view !== view;
      });
      // Cash prize: prize and bank details side by side on desktop.
      this.card.classList.toggle('is-wide', view === 'payout');
      if (view !== 'notstarted') clearInterval(this.countdownTimer);
      if (focus) this.focusView(view);
    }

    focusView(view) {
      const container = this.root.querySelector(`[data-view="${view}"]`);
      const target = container && Array.from(container.querySelectorAll('[data-focus]')).find(
        (element) => element.offsetParent !== null,
      );
      if (target) target.focus({ preventScroll: true });
      if (this.card.getBoundingClientRect().top < 0) this.scrollToCard();
    }

    // Brings the top of the card into view, below the theme's sticky header (scroll-margin-top
    // in the CSS), not the top of the page.
    scrollToCard() {
      this.card.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    }

    announce(message) {
      this.live.textContent = '';
      window.setTimeout(() => {
        this.live.textContent = message;
      }, 50);
    }

    periodState() {
      const now = Date.now();
      const beforeStart = this.start && now < this.start.time;
      if (this.mode === 'open') return 'open';
      if (this.mode === 'closed') return beforeStart ? 'notstarted' : 'ended';
      if (beforeStart) return 'notstarted';
      if (this.end && now >= this.end.time) return 'ended';
      return 'open';
    }

    showForPeriod(focus) {
      const period = this.periodState();
      if (period === 'notstarted') return this.showNotStarted(focus);
      if (period === 'ended') return this.show('ended', { focus });
      return this.show('s1', { focus });
    }

    fillDates() {
      const longDate = { day: 'numeric', month: 'long', year: 'numeric' };
      const startDate = this.start
        ? formatInOffset(this.start.time, this.start.offsetMinutes, this.locale, longDate)
        : '';
      this.fillDateText(this.root.querySelector('[data-date-text="start"]'), startDate);

      let endDate = '';
      if (this.end) {
        // The end is exclusive: the last day is the one just before it.
        const lastMoment = this.end.time - 60000;
        const { offsetMinutes } = this.end;
        endDate = formatInOffset(lastMoment, offsetMinutes, this.locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
        this.root.querySelector('[data-calendar="month"]').textContent = formatInOffset(lastMoment, offsetMinutes, this.locale, { month: 'long' });
        this.root.querySelector('[data-calendar="day"]').textContent = formatInOffset(lastMoment, offsetMinutes, this.locale, { day: 'numeric' });
        this.root.querySelector('[data-calendar="year"]').textContent = formatInOffset(lastMoment, offsetMinutes, this.locale, { year: 'numeric' });
      }
      // Without an end there's nothing to put on the calendar sheet.
      this.root.querySelector('[data-calendar-sheet]').hidden = !this.end;
      this.fillDateText(this.root.querySelector('[data-date-text="end"]'), endDate);
    }

    // Puts the date into a text with "{date}". Without a date (setting not readable) the text
    // without date from data-no-date is shown instead, or without one the element is hidden,
    // so no sentence has a gap.
    fillDateText(element, date) {
      if (date || !element.textContent.includes('{date}')) {
        element.textContent = fill(element.textContent, { date });
      } else if (element.dataset.noDate) {
        element.textContent = element.dataset.noDate;
      } else {
        element.hidden = true;
      }
    }

    // Also shown when the app answers notStarted although the start setting has passed
    // (settings and app disagree): then without countdown and without switching back.
    showNotStarted(focus) {
      this.show('notstarted', { focus });
      const timer = this.root.querySelector('[data-countdown]');
      const counting = Boolean(this.start) && this.start.time > Date.now();
      timer.hidden = !counting;
      if (!counting) return;
      const pad = (value) => String(value).padStart(2, '0');
      let lastLabel = '';
      const tick = () => {
        const remaining = Math.max(0, this.start.time - Date.now());
        if (remaining === 0) {
          clearInterval(this.countdownTimer);
          // What a reload would show now, in every mode ("closed" then shows the end).
          this.showForPeriod(false);
          return;
        }
        const seconds = Math.floor(remaining / 1000);
        const parts = {
          days: Math.floor(seconds / 86400),
          hours: Math.floor((seconds % 86400) / 3600),
          minutes: Math.floor((seconds % 3600) / 60),
          seconds: seconds % 60,
        };
        Object.entries(parts).forEach(([unit, value]) => {
          timer.querySelector(`[data-count="${unit}"]`).textContent = pad(value);
        });
        // The label only changes once a minute, so screen readers aren't flooded.
        const label = fill(this.texts.countdown, parts);
        if (label !== lastLabel) {
          timer.setAttribute('aria-label', label);
          lastLabel = label;
        }
      };
      clearInterval(this.countdownTimer);
      tick();
      this.countdownTimer = setInterval(tick, 1000);
    }

    // ----- Fields and banners ------------------------------------------------------------

    field(form, name) {
      return form.querySelector(`[data-field="${name}"]`);
    }

    input(form, name) {
      return this.field(form, name).querySelector('[data-input]');
    }

    setFieldError(form, name, code) {
      const field = this.field(form, name);
      const input = field.querySelector('[data-input]');
      const hint = field.querySelector('[data-hint]');
      const error = field.querySelector('[data-error]');
      const messages = this.texts.fields[name] || {};
      const message = code ? messages[code] || messages.empty || '' : '';
      field.classList.toggle('has-error', Boolean(code));
      error.hidden = !code;
      error.querySelector('[data-error-text]').textContent = message;
      if (hint) hint.hidden = Boolean(code) || !hint.textContent.trim() || hint.dataset.forceHidden === 'true';
      input.setAttribute('aria-invalid', code ? 'true' : 'false');
      const describedBy = code ? error.id : hint && !hint.hidden ? hint.id : '';
      if (describedBy) input.setAttribute('aria-describedby', describedBy);
      else input.removeAttribute('aria-describedby');
    }

    clearFieldErrors(form) {
      form.querySelectorAll('[data-field]').forEach((field) => {
        if (field.classList.contains('has-error')) this.setFieldError(form, field.dataset.field, null);
      });
    }

    // Shows field errors ({ name: code }), focuses the first one and reads it out.
    showFieldErrors(form, errors) {
      const names = Object.keys(errors);
      names.forEach((name) => this.setFieldError(form, name, errors[name]));
      if (!names.length) return;
      const first = this.input(form, names[0]);
      first.focus();
      const messages = this.texts.fields[names[0]] || {};
      this.announce(messages[errors[names[0]]] || messages.empty || '');
    }

    // tone: "warn" or "error". retry runs the last request again.
    setBanner(form, banner) {
      const element = form.querySelector('[data-banner]');
      if (!banner) {
        element.hidden = true;
        return;
      }
      const [title, text] = this.texts.banners[banner.key] || this.texts.banners.error;
      element.className = `gw100k__banner gw100k__banner--${banner.tone || 'error'}`;
      element.setAttribute('role', banner.tone === 'warn' ? 'status' : 'alert');
      element.querySelector('[data-banner-title]').textContent = title;
      element.querySelector('[data-banner-text]').textContent = text;
      element.querySelector('[data-banner-contact]').hidden = !banner.contact;
      element.querySelector('[data-banner-retry]').hidden = !banner.retry;
      element.hidden = false;
      this.announce(`${title} ${text}`);
    }

    genericError(form, retry = true) {
      this.setBanner(form, { key: 'error', tone: 'error', contact: true, retry });
    }

    // Locks the form after tooManyAttempts until the page is reloaded.
    setLocked(form, locked) {
      form.classList.toggle('is-locked', locked);
      form.querySelector('[data-submit]').setAttribute('aria-disabled', locked ? 'true' : 'false');
    }

    // ----- Events ------------------------------------------------------------------------

    bindEvents() {
      const { s1, s2, pay } = this.forms;

      s1.querySelectorAll('[data-channel]').forEach((button) => {
        button.addEventListener('click', () => this.setChannel(button.dataset.channel, true));
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
          event.preventDefault();
          this.setChannel(this.state.channel === 'online' ? 'tiktok' : 'online', true);
          s1.querySelector(`[data-channel="${this.state.channel}"]`).focus();
        });
      });

      this.root.querySelectorAll('[data-help-toggle]').forEach((toggle) => {
        toggle.addEventListener('click', () => {
          const help = this.root.querySelector(`#${toggle.getAttribute('aria-controls')}`);
          const open = toggle.getAttribute('aria-expanded') !== 'true';
          toggle.setAttribute('aria-expanded', String(open));
          help.hidden = !open;
        });
      });

      // Order number: digits only; the "#" in front is fixed and not part of the input.
      this.sanitizeOnInput(s1, 'orderNumber', (value) => value.replace(/\D/g, '').slice(0, 12));
      // Codes are shown with a hyphen for reading: "3FA-9C1" and "K7XP-4M2Q".
      this.formatOnInput(s1, 'emailCode', {
        clean: normalizeCode,
        format: (code) => groupCode(code, 3),
        maxLength: () => 6,
      });
      this.formatOnInput(s2, 'code', {
        clean: normalizeCode,
        format: (code) => groupCode(code, 4),
        maxLength: () => 8,
      });
      // IBAN in groups of four; spaces typed by the user are dropped, the length is capped
      // at the one of the country.
      this.formatOnInput(pay, 'iban', {
        clean: (value) => String(value || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase(),
        format: formatIban,
        maxLength: (iban) => IBAN_LENGTHS[iban.slice(0, 2)] || IBAN_MAX_LENGTH,
      });
      ['emailCode', 'tiktokOrderId', 'email', 'emailRepeat'].forEach((name) => this.sanitizeOnInput(s1, name, (value) => value));
      this.sanitizeOnInput(s2, 'code', (value) => value);
      this.sanitizeOnInput(pay, 'iban', (value) => value);
      this.sanitizeOnInput(pay, 'accountHolder', (value) => value);
      this.checkIbanWhileTyping();

      const consent = this.input(s2, 'consent');
      consent.addEventListener('change', () => this.updateConsent());

      s1.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submitOrder();
      });
      s2.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submitCode();
      });
      pay.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submitBankDetails();
      });

      s2.querySelector('[data-change]').addEventListener('click', () => {
        this.setBanner(s2, null);
        this.setLocked(s2, false);
        this.show('s1');
      });

      this.root.querySelectorAll('[data-banner-retry]').forEach((button) => {
        button.addEventListener('click', () => {
          if (this.lastAction) this.lastAction();
        });
      });

      this.root.querySelector('[data-token-retry]').addEventListener('click', () => {
        if (this.linkToken) this.loadToken(this.linkToken);
      });

      this.root.querySelector('[data-copy]').addEventListener('click', () => this.copyDiscountCode());
    }

    sanitizeOnInput(form, name, sanitize) {
      const input = this.input(form, name);
      input.addEventListener('input', () => {
        const clean = sanitize(input.value);
        if (clean !== input.value) input.value = clean;
        if (this.field(form, name).classList.contains('has-error')) this.setFieldError(form, name, null);
        // The lock in step 1 belongs to the order entered; another order may be tried.
        if (form === this.forms.s1) this.unlockStep1();
      });
    }

    // Formats a field while typing (separators set by `format`, never typed by the user)
    // and keeps the cursor where the user is typing. `clean` removes everything that isn't
    // part of the value, `maxLength` gets the cleaned value.
    formatOnInput(form, name, { clean, format, maxLength }) {
      const input = this.input(form, name);
      let previous = '';
      input.addEventListener('beforeinput', () => {
        previous = clean(input.value);
      });
      input.addEventListener('input', (event) => {
        const caret = input.selectionStart ?? input.value.length;
        let before = clean(input.value.slice(0, caret));
        let value = clean(input.value);
        // Deleting a separator alone would change nothing: delete the character next to it.
        if (value === previous && event.inputType === 'deleteContentBackward' && before.length > 0) {
          value = value.slice(0, before.length - 1) + value.slice(before.length);
          before = before.slice(0, -1);
        } else if (value === previous && event.inputType === 'deleteContentForward') {
          value = value.slice(0, before.length) + value.slice(before.length + 1);
        }
        value = value.slice(0, maxLength(value));
        before = before.slice(0, value.length);
        input.value = format(value);
        const position = format(before).length;
        if (document.activeElement === input) input.setSelectionRange(position, position);
      });
    }

    // Shows IBAN errors that are already certain while typing (country outside SEPA, wrong
    // check digits once complete) and checks the whole IBAN when leaving the field.
    checkIbanWhileTyping() {
      const form = this.forms.pay;
      const input = this.input(form, 'iban');
      input.addEventListener('input', () => {
        const error = validateIbanWhileTyping(input.value);
        if (error) this.setFieldError(form, 'iban', error);
      });
      input.addEventListener('blur', () => {
        if (!normalizeIban(input.value)) return;
        const error = validateIban(input.value);
        if (error) this.setFieldError(form, 'iban', error);
      });
    }

    unlockStep1() {
      const form = this.forms.s1;
      if (!form.classList.contains('is-locked')) return;
      this.setLocked(form, false);
      this.setBanner(form, null);
    }

    setChannel(channel, userAction) {
      const form = this.forms.s1;
      this.state.channel = channel;
      form.querySelectorAll('[data-channel]').forEach((button) => {
        const selected = button.dataset.channel === channel;
        button.setAttribute('aria-checked', String(selected));
        button.tabIndex = selected ? 0 : -1;
      });
      form.querySelectorAll('[data-channel-fields]').forEach((fields) => {
        fields.hidden = fields.dataset.channelFields !== channel;
      });
      if (userAction) {
        this.clearFieldErrors(form);
        this.setBanner(form, null);
        this.setLocked(form, false);
      }
    }

    // Also clears a consent error. The hint goes once the box is checked: setFieldError hides it
    // then and points aria-describedby at it only while it's visible.
    updateConsent() {
      const form = this.forms.s2;
      const checked = this.input(form, 'consent').checked;
      this.field(form, 'consent').querySelector('[data-hint]').dataset.forceHidden = String(checked);
      this.setFieldError(form, 'consent', null);
      if (!form.classList.contains('is-locked')) {
        form.querySelector('[data-submit]').setAttribute('aria-disabled', checked ? 'false' : 'true');
      }
    }

    // ----- Step 1: check the order -------------------------------------------------------

    readOrder() {
      const form = this.forms.s1;
      if (this.state.channel === 'tiktok') {
        return {
          channel: 'tiktok',
          tiktokOrderId: this.input(form, 'tiktokOrderId').value.trim(),
          email: this.input(form, 'email').value.trim(),
          emailRepeat: this.input(form, 'emailRepeat').value.trim(),
        };
      }
      const digits = this.input(form, 'orderNumber').value.replace(/\D/g, '');
      return {
        channel: 'online',
        orderName: digits ? `#${digits}` : '',
        emailCode: normalizeCode(this.input(form, 'emailCode').value),
      };
    }

    validateOrder(order) {
      const errors = {};
      if (order.channel === 'tiktok') {
        if (!order.tiktokOrderId) errors.tiktokOrderId = 'empty';
        if (!order.email) errors.email = 'empty';
        else if (!EMAIL_PATTERN.test(order.email)) errors.email = 'format';
        if (!order.emailRepeat) errors.emailRepeat = 'empty';
        else if (order.email && order.email.toLowerCase() !== order.emailRepeat.toLowerCase()) {
          errors.emailRepeat = 'mismatch';
        }
      } else {
        if (!order.orderName) errors.orderNumber = 'empty';
        if (!order.emailCode) errors.emailCode = 'empty';
        else if (!EMAIL_CODE_PATTERN.test(order.emailCode)) errors.emailCode = 'format';
      }
      return errors;
    }

    // The fields the app takes for the order, per channel.
    orderBody(order) {
      if (order.channel === 'tiktok') {
        return { channel: 'tiktok', tiktokOrderId: order.tiktokOrderId, email: order.email };
      }
      return { channel: 'online', orderName: order.orderName, emailCode: order.emailCode };
    }

    async submitOrder() {
      const form = this.forms.s1;
      if (this.state.busy || form.classList.contains('is-locked')) return;
      this.lastAction = () => this.submitOrder();
      const order = this.readOrder();
      this.clearFieldErrors(form);
      this.setBanner(form, null);
      const errors = this.validateOrder(order);
      if (Object.keys(errors).length) {
        this.showFieldErrors(form, errors);
        return;
      }

      const result = await this.run(form, 'verify-order', this.orderBody(order));
      if (!result) return;
      if (result.ok) {
        this.goToStep2(order);
        return;
      }
      switch (result.error) {
        case 'wrongEmailCode':
          return this.showFieldErrors(form, { emailCode: 'wrongEmailCode' });
        case 'unknownOrder':
          return this.showFieldErrors(form, { tiktokOrderId: 'unknownOrder' });
        case 'invalidEmail':
          return this.showFieldErrors(form, { email: 'invalidEmail' });
        case 'orderAlreadyUsed':
          // One participation per order, also with the same card again: the prize went out by
          // email, the page doesn't show it a second time.
          return this.setBanner(form, { key: 'orderAlreadyUsed', tone: 'warn', contact: true });
        case 'tooManyAttempts':
          this.setLocked(form, true);
          return this.setBanner(form, { key: 'tooManyAttempts', tone: 'warn' });
        case 'notStarted':
          return this.showNotStarted(true);
        case 'ended':
          return this.show('ended');
        case 'invalidInput':
          return this.setBanner(form, { key: 'invalidInput', tone: 'error' });
        default:
          // unavailable, invalidRequest, notInstalled, methodNotAllowed, network, unknown
          return this.genericError(form);
      }
    }

    goToStep2(order) {
      const form = this.forms.s2;
      this.state.order = order;
      const label = form.querySelector('[data-confirmed-label]');
      label.textContent = order.channel === 'tiktok' ? label.dataset.labelTiktok : label.dataset.labelOnline;
      form.querySelector('[data-confirmed-value]').textContent =
        order.channel === 'tiktok' ? order.tiktokOrderId : order.orderName;
      this.input(form, 'code').value = '';
      this.clearFieldErrors(form);
      this.setLocked(form, false);
      this.updateConsent();
      this.show('s2');
      this.setBanner(form, null);
    }

    // ----- Step 2: redeem the card code --------------------------------------------------

    async submitCode() {
      const form = this.forms.s2;
      if (this.state.busy || form.classList.contains('is-locked')) return;
      this.lastAction = () => this.submitCode();
      const codeInput = this.input(form, 'code');
      const code = normalizeCode(codeInput.value);
      codeInput.value = groupCode(code, 4);
      this.clearFieldErrors(form);

      const errors = {};
      if (!code) errors.code = 'empty';
      else if (!CARD_CODE_PATTERN.test(code)) errors.code = 'format';
      if (!this.input(form, 'consent').checked) errors.consent = 'missing';
      if (Object.keys(errors).length) {
        this.showFieldErrors(form, errors);
        return;
      }
      this.setBanner(form, null);

      const result = await this.run(form, 'redeem', { ...this.orderBody(this.state.order), code });
      if (!result) return;
      if (result.ok) {
        this.showPrize(result);
        return;
      }
      switch (result.error) {
        case 'invalidCode':
          return this.showFieldErrors(form, { code: 'invalidCode' });
        case 'alreadyRedeemed':
          return this.setBanner(form, { key: 'alreadyRedeemed', tone: 'warn' });
        case 'orderAlreadyUsed':
          return this.setBanner(form, { key: 'orderAlreadyUsed', tone: 'warn', contact: true });
        case 'tooManyAttempts':
          this.setLocked(form, true);
          return this.setBanner(form, { key: 'tooManyAttempts', tone: 'warn' });
        case 'discountUnavailable':
          return this.setBanner(form, { key: 'discountUnavailable', tone: 'warn', retry: true });
        case 'deadlinePassed':
          return this.setBanner(form, { key: 'deadlinePassed', tone: 'error', contact: true });
        case 'wrongEmailCode':
        case 'unknownOrder':
        case 'invalidEmail':
          // The order data no longer fits (e.g. changed meanwhile): back to step 1.
          this.show('s1');
          return this.submitOrderError(result.error);
        case 'notStarted':
          return this.showNotStarted(true);
        case 'ended':
          return this.show('ended');
        case 'invalidInput':
          return this.setBanner(form, { key: 'invalidInput', tone: 'error' });
        default:
          return this.genericError(form);
      }
    }

    submitOrderError(error) {
      const fieldErrors = {
        wrongEmailCode: { emailCode: 'wrongEmailCode' },
        unknownOrder: { tiktokOrderId: 'unknownOrder' },
        invalidEmail: { email: 'invalidEmail' },
      };
      this.showFieldErrors(this.forms.s1, fieldErrors[error]);
    }

    showPrize(result) {
      const prize = result.prize || {};
      if (prize.type === 'discount') {
        this.showDiscount(prize.discountCode);
      } else if (prize.type === 'cash') {
        this.state.amountCents = prize.amountCents;
        // Without a token the bank details are in already.
        this.state.bankDetailsToken = result.bankDetailsToken || '';
        this.showPayout(this.state.bankDetailsToken ? 'form' : 'already');
        this.celebrate();
      } else {
        // An answer this page doesn't know: no prize frame without a prize.
        this.genericError(this.forms.s2);
        return;
      }
      // The result replaces the long step 2: glide back up to where it starts.
      this.scrollToCard();
    }

    // ----- Consolation prize -------------------------------------------------------------

    showDiscount(discountCode) {
      const view = this.root.querySelector('[data-view="discount"]');
      const button = view.querySelector('[data-copy]');
      view.querySelector('[data-discount-code]').textContent = discountCode;
      button.dataset.code = discountCode;
      button.setAttribute('aria-label', fill(button.dataset.label, { code: discountCode }));
      button.classList.remove('is-copied');
      const message = view.querySelector('[data-copy-msg]');
      message.textContent = message.dataset.idle;
      view.querySelector('[data-shop-with-code]').href =
        `/discount/${encodeURIComponent(discountCode)}?redirect=${encodeURIComponent(window.Shopify && Shopify.routes ? Shopify.routes.root : '/')}`;
      this.show('discount');
    }

    async copyDiscountCode() {
      const button = this.root.querySelector('[data-copy]');
      const message = this.root.querySelector('[data-copy-msg]');
      const code = button.dataset.code || '';
      let copied = false;
      try {
        await navigator.clipboard.writeText(code);
        copied = true;
      } catch (error) {
        copied = this.copyWithSelection(code);
      }
      button.classList.toggle('is-copied', copied);
      message.textContent = copied ? message.dataset.done : message.dataset.failed;
    }

    copyWithSelection(text) {
      const helper = document.createElement('textarea');
      helper.value = text;
      helper.setAttribute('readonly', '');
      helper.className = 'gw100k__clipboard-helper';
      document.body.appendChild(helper);
      helper.select();
      let copied = false;
      try {
        copied = document.execCommand('copy');
      } catch (error) {
        copied = false;
      }
      helper.remove();
      return copied;
    }

    // ----- Cash prize and bank details ---------------------------------------------------

    // body: "form", "done", "already" (bank details in) or "closed" (link can't be used any
    // more, see showPayoutClosed)
    showPayout(body, { ibanMasked = '', focus = true } = {}) {
      const view = this.root.querySelector('[data-view="payout"]');
      const amount = formatAmount(this.state.amountCents, this.locale);

      view.querySelector('[data-amount]').textContent = amount;
      const amountText = view.querySelector('[data-amount-text]');
      amountText.textContent = fill(amountText.dataset.amountText, { amount });
      const payLabel = view.querySelector('[data-pay-label]');
      payLabel.textContent = fill(payLabel.dataset.payLabel, { amount });

      const form = this.forms.pay;
      form.hidden = body !== 'form';
      view.querySelector('[data-pay-done]').hidden = body !== 'done';
      view.querySelector('[data-pay-already]').hidden = body !== 'already';
      view.querySelector('[data-pay-closed]').hidden = body !== 'closed';
      if (body === 'form') {
        this.setBanner(form, null);
        this.clearFieldErrors(form);
      }
      if (body === 'done') {
        const title = view.querySelector('[data-done-title]');
        title.textContent = fill(title.dataset.doneTitle, { iban: ibanMasked });
      }

      if (this.state.view === 'payout') {
        // Same view, other body: focus what changed instead of the prize again.
        const status = body === 'form' ? null : view.querySelector(`[data-pay-${body}]`);
        const message = status && status.querySelector('.gw100k__success, [data-banner]');
        if (message) {
          message.setAttribute('tabindex', '-1');
          message.focus();
        }
        return;
      }
      this.show('payout', { focus });
    }

    async submitBankDetails() {
      const form = this.forms.pay;
      if (this.state.busy) return;
      this.lastAction = () => this.submitBankDetails();
      const ibanInput = this.input(form, 'iban');
      const holderInput = this.input(form, 'accountHolder');
      ibanInput.value = formatIban(ibanInput.value);
      const iban = normalizeIban(ibanInput.value);
      const accountHolder = holderInput.value.trim();
      this.clearFieldErrors(form);
      this.setBanner(form, null);

      const errors = {};
      const ibanError = validateIban(iban);
      if (ibanError) errors.iban = ibanError;
      const holderError = validateAccountHolder(accountHolder);
      if (holderError) errors.accountHolder = holderError;
      if (Object.keys(errors).length) {
        this.showFieldErrors(form, errors);
        return;
      }

      const result = await this.run(form, 'bank-details', {
        token: this.state.bankDetailsToken,
        iban,
        accountHolder,
      });
      if (!result) return;
      if (result.ok) {
        this.finishBankDetails();
        // Nothing of the bank details stays on the page.
        ibanInput.value = '';
        holderInput.value = '';
        this.showPayout('done', { ibanMasked: result.ibanMasked || '' });
        return;
      }
      switch (result.error) {
        case 'invalidIban':
        case 'ibanNotSepa':
          return this.showFieldErrors(form, { iban: result.error });
        case 'invalidAccountHolder':
          return this.showFieldErrors(form, { accountHolder: 'invalidAccountHolder' });
        case 'linkUsed':
          this.finishBankDetails();
          return this.showPayout('already');
        case 'invalidLink':
        case 'deadlinePassed':
          this.finishBankDetails();
          return this.showPayoutClosed(result.error);
        case 'invalidInput':
          return this.setBanner(form, { key: 'invalidInput', tone: 'error' });
        case 'noCashPrize':
          return this.genericError(form, false);
        default:
          return this.genericError(form);
      }
    }

    // The link can't be used any more (deadline passed, invalid): instead of the form only the
    // reason and the way to support, so nothing is sent again without a token.
    showPayoutClosed(key, { focus = true } = {}) {
      this.setBanner(this.root.querySelector('[data-pay-closed]'), { key, tone: 'error', contact: true });
      this.showPayout('closed', { focus });
    }

    // The token can't be used any more: forget it.
    finishBankDetails() {
      this.state.bankDetailsToken = '';
      tokenStore.clear();
    }

    // ----- Bank details link from the email ----------------------------------------------

    async loadToken(token) {
      if (this.state.busy) return;
      this.state.busy = true;
      // Kept here as well: without sessionStorage the retry would have no token otherwise, and
      // the address bar doesn't have it any more.
      this.linkToken = token;
      this.show('token-loading', { focus: this.state.view !== 'boot' });
      let result;
      try {
        result = await this.request('bank-details-link', { token });
      } finally {
        this.state.busy = false;
      }
      if (result.ok) {
        // The same page as right after redeeming: a token from the link or from redeem.
        this.state.bankDetailsToken = token;
        this.state.amountCents = result.amountCents;
        this.showPayout('form');
        this.celebrate();
        return;
      }
      // Links don't expire one by one: they're valid until the IBAN deadline (deadlinePassed).
      // Without a token or for a discount prize there's nothing to enter either: invalid.
      const keys = {
        linkUsed: 'linkUsed',
        deadlinePassed: 'deadlinePassed',
        invalidLink: 'invalidLink',
        invalidInput: 'invalidLink',
        noCashPrize: 'invalidLink',
      };
      const key = keys[result.error] || 'error';
      // A link that can't be used any more isn't asked for again on the next visit.
      if (key !== 'error') tokenStore.clear();
      this.showTokenError(key);
    }

    showTokenError(key) {
      const view = this.root.querySelector('[data-view="token-error"]');
      const [title, text] = this.texts.token[key] || this.texts.token.error;
      view.querySelector('[data-token-error-title]').textContent = title;
      view.querySelector('[data-token-error-text]').textContent = text;
      view.querySelector('[data-token-retry]').hidden = key !== 'error';
      this.show('token-error');
    }

    // ----- Confetti and money rain -------------------------------------------------------

    // A burst out of the amount, then two waves of confetti, coins and notes over the whole
    // cash prize. Positions, sizes and paths are random per piece; only transforms animate.
    celebrate() {
      if (prefersReducedMotion()) return;
      const container = this.root.querySelector('[data-confetti]');
      container.textContent = '';
      const area = container.getBoundingClientRect();
      const amount = this.root.querySelector('[data-view="payout"] .gw100k__prize').getBoundingClientRect();
      const origin = {
        x: amount.left - area.left + amount.width / 2,
        y: amount.top - area.top + amount.height / 2,
      };
      const random = (min, max) => min + Math.random() * (max - min);
      const colors = ['#f2c342', '#000000', '#f6e3a1', '#c49a3a', '#d9d9d9', '#f2c342'];
      const fragment = document.createDocumentFragment();

      const addPiece = (index, burst) => {
        const piece = document.createElement('span');
        const roll = Math.random();
        const kind = roll < 0.22 ? 'coin' : roll < 0.4 ? 'note' : 'paper';
        piece.className = `gw100k__piece gw100k__piece--${kind}${burst ? ' gw100k__piece--burst' : ''}`;
        const set = (name, value) => piece.style.setProperty(name, value);
        if (kind === 'paper') {
          set('--gw-w', `${Math.round(random(10, 20))}px`);
          set('--gw-h', `${Math.round(random(6, 12))}px`);
          piece.style.background = colors[index % colors.length];
        } else {
          set('--gw-w', `${Math.round(kind === 'coin' ? random(22, 34) : random(40, 58))}px`);
          piece.textContent = '€';
        }
        if (burst) {
          piece.style.left = `${origin.x}px`;
          piece.style.top = `${origin.y}px`;
          set('--gw-bx', `${Math.round(random(-1, 1) * area.width * 0.45)}px`);
          set('--gw-by', `${Math.round(-random(80, 220))}px`);
          set('--gw-fall', `${Math.round(area.height - origin.y + 80)}px`);
          set('--gw-delay', `${random(0, 0.15).toFixed(2)}s`);
          set('--gw-duration', `${random(2.6, 3.6).toFixed(2)}s`);
        } else {
          piece.style.left = `${random(-2, 98)}%`;
          set('--gw-fall', `${Math.round(area.height + 120)}px`);
          set('--gw-delay', `${(index < 50 ? random(0, 0.8) : random(1, 2)).toFixed(2)}s`);
          set('--gw-duration', `${random(3, 4.6).toFixed(2)}s`);
        }
        set('--gw-drift', `${Math.round(random(-70, 70))}px`);
        set('--gw-rot', `${Math.round(random(0, 360))}deg`);
        set('--gw-spin', `${Math.round(random(-540, 540))}deg`);
        set('--gw-flip', `${Math.round(random(360, 1080))}deg`);
        fragment.appendChild(piece);
      };

      for (let index = 0; index < 40; index += 1) addPiece(index, true);
      for (let index = 0; index < 85; index += 1) addPiece(index, false);
      container.appendChild(fragment);
      clearTimeout(this.confettiTimer);
      this.confettiTimer = setTimeout(() => {
        container.textContent = '';
      }, 7500);
    }

    // ----- Theme editor preview ----------------------------------------------------------

    showPreview(state) {
      const sampleOrder = { channel: 'online', orderName: '#10234', emailCode: '3F9A1C' };
      const sampleTiktok = { channel: 'tiktok', tiktokOrderId: '577012345678901234', email: 'alex@beispiel.de' };
      this.state.amountCents = 25000;
      switch (state) {
        case 'notstarted':
          return this.showNotStarted(false);
        case 'ended':
          return this.show('ended', { focus: false });
        case 't1':
          this.setChannel('tiktok', false);
          return this.show('s1', { focus: false });
        case 's1-error':
          this.show('s1', { focus: false });
          return this.genericError(this.forms.s1);
        case 's2':
          this.goToStep2(sampleOrder);
          return;
        case 's2-tiktok':
          this.goToStep2(sampleTiktok);
          return;
        case 's2-locked':
          this.goToStep2(sampleOrder);
          this.setLocked(this.forms.s2, true);
          return this.setBanner(this.forms.s2, { key: 'tooManyAttempts', tone: 'warn' });
        case 'discount':
          return this.showDiscount('ELUSA-7QX4-M2PZ');
        case 'cash':
          this.state.bankDetailsToken = 'preview';
          this.showPayout('form', { focus: false });
          return this.celebrate();
        case 'cash-already':
          return this.showPayout('already', { focus: false });
        case 'cash-done':
          return this.showPayout('done', { ibanMasked: 'DE89 … 3000', focus: false });
        case 'cash-closed':
          return this.showPayoutClosed('deadlinePassed', { focus: false });
        case 'token-loading':
          return this.show('token-loading', { focus: false });
        case 'token-used':
          return this.showTokenError('linkUsed');
        case 'token-invalid':
          return this.showTokenError('invalidLink');
        case 'token-deadline':
          return this.showTokenError('deadlinePassed');
        default:
          return this.show('s1', { focus: false });
      }
    }
  }

  // ---------------------------------------------------------------------------------------

  const instances = new Map();

  function init(scope) {
    scope.querySelectorAll('[data-gw100k]').forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new Aktion100k(root));
    });
  }

  function teardown(scope) {
    instances.forEach((instance, root) => {
      if (scope.contains(root)) {
        instance.destroy();
        instances.delete(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // Theme editor: sections are re-rendered when settings change.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => teardown(event.target));
})();
